import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useVehicleImage } from '../hooks/useVehicleImage';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Car, ArrowRight, Fuel, Settings2, Calendar, ChevronRight, Gauge, ImageIcon, SlidersHorizontal, Euro, Route, Clock, GraduationCap, Users, RotateCcw, Sparkles } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import OpzionatoBadge from '../components/OpzionatoBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [36, 48, 60];
const KM_OPTIONS = [10000, 15000, 20000, 25000];
const BUDGET_TOLERANCE = 50; // ±€50

// Key for sessionStorage
const CATALOG_FILTERS_KEY = 'catalog_filters';
const CATALOG_SCROLL_KEY = 'catalog_scroll';

// Helper per tracciare eventi KPI
const trackEvent = async (eventType, vehicleId, getAuthHeader) => {
  try {
    await axios.post(`${API}/track-event`, {
      event_type: eventType,
      vehicle_id: vehicleId
    }, {
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
    });
  } catch (error) {
    // Silently fail - tracking non deve bloccare l'UX
    console.debug('Tracking event failed:', error);
  }
};

// Helper to get saved filters from sessionStorage
const getSavedFilters = () => {
  try {
    const saved = sessionStorage.getItem(CATALOG_FILTERS_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// Helper to save filters to sessionStorage
const saveFilters = (filters) => {
  try {
    sessionStorage.setItem(CATALOG_FILTERS_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
};

// Helper to clear filters
const clearSavedFilters = () => {
  try {
    sessionStorage.removeItem(CATALOG_FILTERS_KEY);
    sessionStorage.removeItem(CATALOG_SCROLL_KEY);
  } catch {
    // Ignore
  }
};

// Componente immagine veicolo Hero per card catalogo (solo NUOVO)
const CatalogVehicleHero = ({ marca, modello, isUsato }) => {
  const { imageUrl } = useVehicleImage(marca, isUsato ? null : modello);
  if (!imageUrl || isUsato) return null;
  return (
    <div className="w-full h-32 bg-gradient-to-b from-white to-slate-50 flex items-center justify-center px-4 py-2">
      <img src={imageUrl} alt={`${marca} ${modello}`} className="max-w-full max-h-full object-contain drop-shadow-md" loading="lazy" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
    </div>
  );
};

// Card Promo per il Catalogo (stile giallo distintivo)
const PromoCardCatalog = ({ promo, navigate }) => {
  const { imageUrl } = useVehicleImage(promo.marca, promo.modello);
  const heroImg = promo.link_immagine_effective || promo.link_immagine || imageUrl;
  const quotes = promo.quotations ? Object.values(promo.quotations).filter(v => v > 0) : [];
  const minQuote = quotes.length > 0 ? Math.min(...quotes) : null;
  const maxQuote = quotes.length > 0 ? Math.max(...quotes) : null;

  return (
    <Card
      className="group overflow-hidden border-2 border-amber-400 ring-1 ring-amber-200 card-hover cursor-pointer"
      onClick={() => navigate(`/dashboard/offerta-mese/${promo.id}`)}
      data-testid={`promo-catalog-${promo.id}`}
    >
      <CardContent className="p-0">
        {/* Hero Image */}
        {heroImg && (
          <div className="w-full h-32 bg-gradient-to-b from-amber-50 to-white flex items-center justify-center px-4 py-2">
            <img src={heroImg} alt={`${promo.marca} ${promo.modello}`} className="max-w-full max-h-full object-contain drop-shadow-md" loading="lazy" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-3 bg-gradient-to-br from-amber-50 to-amber-100/50 relative">
          <div className="absolute top-2 right-2">
            <Badge className="bg-amber-500 text-white text-[10px] px-2 py-0.5 font-bold shadow-md">OFFERTA DEL MESE</Badge>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium text-amber-700">{promo.marca}</p>
          </div>
          <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {promo.modello}
          </h3>
          {promo.versione && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{promo.versione}</p>}
        </div>

        {/* Info + Prezzo Range */}
        <div className="px-6 py-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {promo.alimentazione && <Badge variant="secondary" className="text-xs">{promo.alimentazione}</Badge>}
            {promo.cambio && <Badge variant="secondary" className="text-xs">{promo.cambio}</Badge>}
            {promo.listino && <Badge className="bg-slate-700 text-white text-[10px]">{promo.listino}</Badge>}
          </div>
          {minQuote && (
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              {minQuote === maxQuote ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-amber-700">Canone</span>
                  <div>
                    <span className="text-2xl font-bold text-amber-700">€{Math.round(minQuote)}</span>
                    <span className="text-sm text-amber-600">/mese</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-amber-700">Canone</span>
                  <div className="text-right">
                    <span className="text-sm text-amber-600">Da </span>
                    <span className="text-xl font-bold text-amber-700">€{Math.round(minQuote)}</span>
                    <span className="text-sm text-amber-600"> a </span>
                    <span className="text-xl font-bold text-amber-700">€{Math.round(maxQuote)}</span>
                    <span className="text-sm text-amber-600">/mese</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CatalogPage = () => {
  const { getAuthHeader } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [promoOffers, setPromoOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRestored = useRef(false);
  const isInitialMount = useRef(true);
  const filtersRestored = useRef(false);
  
  // Get saved filters on mount
  const savedFilters = useMemo(() => getSavedFilters(), []);
  const hasSavedFilters = useMemo(() => !!savedFilters, [savedFilters]);
  
  // Tipo veicolo: nuovo o usato
  const [tipoVeicolo, setTipoVeicolo] = useState(savedFilters?.tipoVeicolo || 'nuovo');
  
  // Available options (filtered dynamically)
  const [availableMarche, setAvailableMarche] = useState([]);
  const [availableModelli, setAvailableModelli] = useState([]);
  const [availableVersioni, setAvailableVersioni] = useState([]);
  const [availableFuels, setAvailableFuels] = useState([]);
  const [availableSegmenti, setAvailableSegmenti] = useState([]);
  const [availableSegmentiMercato, setAvailableSegmentiMercato] = useState([]); // Sottocategorie (NUOVO)
  const [availableSegmentDescriptions, setAvailableSegmentDescriptions] = useState([]); // Sottocategorie (USATO)
  const [availableFuelCodes, setAvailableFuelCodes] = useState([]); // Dettaglio tecnico fuel (NUOVO)
  
  // Selected values - inizializzati da savedFilters
  const [selectedMarca, setSelectedMarca] = useState(savedFilters?.selectedMarca || '');
  const [selectedModello, setSelectedModello] = useState(savedFilters?.selectedModello || '');
  const [selectedVersione, setSelectedVersione] = useState(savedFilters?.selectedVersione || '');
  
  // Personalizza la tua richiesta
  const [useCustomSearch, setUseCustomSearch] = useState(savedFilters?.useCustomSearch || false);
  const [customKm, setCustomKm] = useState(savedFilters?.customKm || '');
  const [customDurata, setCustomDurata] = useState(savedFilters?.customDurata || '');
  const [customFuel, setCustomFuel] = useState(savedFilters?.customFuel || '');
  const [customBudget, setCustomBudget] = useState(savedFilters?.customBudget || '');
  const [customNeopatentati, setCustomNeopatentati] = useState(savedFilters?.customNeopatentati || false);
  const [custom4Vantage, setCustom4Vantage] = useState(savedFilters?.custom4Vantage || false);
  const [customSegmento, setCustomSegmento] = useState(savedFilters?.customSegmento || '');
  const [showNuoviArrivi, setShowNuoviArrivi] = useState(savedFilters?.showNuoviArrivi || false);  // NUOVO: Filtro Nuovi Arrivi
  
  // Filtered vehicles based on custom search
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  
  // All vehicles for budget filtering (without marca/modello filter)
  const [allVehiclesForBudget, setAllVehiclesForBudget] = useState([]);

  // Save filters whenever they change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    
    const filters = {
      tipoVeicolo,
      selectedMarca,
      selectedModello,
      selectedVersione,
      useCustomSearch,
      customKm,
      customDurata,
      customFuel,
      customBudget,
      customNeopatentati,
      custom4Vantage,
      customSegmento,
      showNuoviArrivi  // NUOVO: Salva filtro Nuovi Arrivi
    };
    saveFilters(filters);
  }, [tipoVeicolo, selectedMarca, selectedModello, selectedVersione, useCustomSearch, customKm, customDurata, customFuel, customBudget, customNeopatentati, custom4Vantage, customSegmento, showNuoviArrivi]);

  // Save scroll position before navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(CATALOG_SCROLL_KEY, window.scrollY.toString());
    };
    
    // Save scroll on any navigation
    const saveScroll = () => {
      sessionStorage.setItem(CATALOG_SCROLL_KEY, window.scrollY.toString());
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Save scroll periodically and on scroll end
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(saveScroll, 100);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Restore scroll position after vehicles are loaded
  useEffect(() => {
    if (!loading && vehicles.length > 0 && !scrollRestored.current) {
      const savedScroll = sessionStorage.getItem(CATALOG_SCROLL_KEY);
      if (savedScroll) {
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScroll, 10));
          scrollRestored.current = true;
        }, 100);
      }
    }
  }, [loading, vehicles]);

  // Mark as initialized after first render
  useEffect(() => {
    // On first mount, mark as not initial after a short delay to allow filters to restore
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Reset filters function
  const handleResetFilters = () => {
    setTipoVeicolo('nuovo');
    setSelectedMarca('');
    setSelectedModello('');
    setSelectedVersione('');
    setUseCustomSearch(false);
    setCustomKm('');
    setCustomDurata('');
    setCustomFuel('');
    setCustomBudget('');
    setCustomNeopatentati(false);
    setCustom4Vantage(false);
    setCustomSegmento('');
    setShowNuoviArrivi(false);  // NUOVO: Reset filtro Nuovi Arrivi
    clearSavedFilters();
    window.scrollTo(0, 0);
  };

  // Check if any filter is active
  const hasActiveFilters = selectedMarca || selectedModello || selectedVersione || 
    useCustomSearch || customKm || customDurata || customFuel || customBudget || 
    customNeopatentati || custom4Vantage || customSegmento;

  // Fetch ALL vehicles of this tipo (for budget-based filtering of dropdowns)
  const fetchAllVehiclesForBudget = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicles?tipo=${tipoVeicolo}`, { 
        headers: getAuthHeader() 
      });
      setAllVehiclesForBudget(response.data);
    } catch (error) {
      console.error('Error fetching all vehicles for budget:', error);
    }
  }, [getAuthHeader, tipoVeicolo]);

  // Fetch marche filtered by tipo
  const fetchMarche = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicles/filters?tipo=${tipoVeicolo}`, { headers: getAuthHeader() });
      setAvailableMarche(response.data.marche || []);
      setAvailableFuels(response.data.fuels || []);
      setAvailableSegmenti(response.data.segmenti || []);
      // Reset sotto-categorie (verranno caricate quando si seleziona un segmento)
      setAvailableSegmentiMercato([]);
      setAvailableSegmentDescriptions([]);
      setAvailableFuelCodes(response.data.fuel_codes || []); // Dettaglio tecnico fuel
    } catch (error) {
      console.error('Error fetching marche:', error);
    }
  }, [getAuthHeader, tipoVeicolo]);

  // Fetch sotto-categorie filtrate per segmento selezionato
  const fetchSottocategorie = useCallback(async (segmento) => {
    if (!segmento) {
      setAvailableSegmentiMercato([]);
      setAvailableSegmentDescriptions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${API}/vehicles/filters?tipo=${tipoVeicolo}&segmento=${encodeURIComponent(segmento)}`, 
        { headers: getAuthHeader() }
      );
      setAvailableSegmentiMercato(response.data.segmenti_mercato || []);
      setAvailableSegmentDescriptions(response.data.segment_descriptions || []);
    } catch (error) {
      console.error('Error fetching sottocategorie:', error);
    }
  }, [getAuthHeader, tipoVeicolo]);

  // Fetch modelli when marca changes
  const fetchModelli = useCallback(async (marca) => {
    if (!marca) {
      setAvailableModelli([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/vehicles/filters?tipo=${tipoVeicolo}&marca=${encodeURIComponent(marca)}`, { 
        headers: getAuthHeader() 
      });
      setAvailableModelli(response.data.modelli || []);
    } catch (error) {
      console.error('Error fetching modelli:', error);
    }
  }, [getAuthHeader, tipoVeicolo]);

  // Fetch versioni when modello changes
  const fetchVersioni = useCallback(async (marca, modello) => {
    if (!marca || !modello) {
      setAvailableVersioni([]);
      return;
    }
    try {
      const response = await axios.get(
        `${API}/vehicles/filters?tipo=${tipoVeicolo}&marca=${encodeURIComponent(marca)}&modello=${encodeURIComponent(modello)}`, 
        { headers: getAuthHeader() }
      );
      setAvailableVersioni(response.data.versioni || []);
    } catch (error) {
      console.error('Error fetching versioni:', error);
    }
  }, [getAuthHeader, tipoVeicolo]);

  // Fetch vehicles based on selection
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      
      // Se 4Vantage è attivo in "Personalizza la tua richiesta", usa endpoint dedicato
      // Questo mostra i 104 veicoli del listino 4Vantage con accorpamento
      if (useCustomSearch && custom4Vantage) {
        const params = new URLSearchParams();
        if (selectedMarca) params.append('marca', selectedMarca);
        if (selectedModello) params.append('modello', selectedModello);
        if (customFuel) params.append('fuel', customFuel);
        if (customSegmento) params.append('segmento', customSegmento);
        if (customBudget) {
          const budget = parseFloat(customBudget);
          params.append('budget_min', budget - BUDGET_TOLERANCE);
          params.append('budget_max', budget + BUDGET_TOLERANCE);
        }
        
        response = await axios.get(`${API}/vehicles/4vantage/catalog?${params.toString()}`, { 
          headers: getAuthHeader() 
        });
      } else {
        // Catalogo standard NUOVO/USATO (nessun doppione 4Vantage)
        const params = new URLSearchParams();
        params.append('tipo', tipoVeicolo);
        if (selectedMarca) params.append('marca', selectedMarca);
        if (selectedModello) params.append('modello', selectedModello);
        if (selectedVersione) params.append('versione', selectedVersione);
        if (showNuoviArrivi) params.append('nuovi_arrivi', 'true');
        
        response = await axios.get(`${API}/vehicles?${params.toString()}`, { 
          headers: getAuthHeader() 
        });
      }
      
      setVehicles(response.data);
      
      // Fetch promo offerte del mese pubblicate nel catalogo (solo per NUOVO standard).
      // Con 4Vantage attivo, il catalogo deve mostrare esclusivamente i veicoli 4Vantage:
      // le card promo mensili devono essere nascoste.
      if (tipoVeicolo === 'nuovo' && !(useCustomSearch && custom4Vantage)) {
        try {
          const promoParams = new URLSearchParams();
          if (selectedMarca) promoParams.append('marca', selectedMarca);
          if (selectedModello) promoParams.append('modello', selectedModello);
          const promoRes = await axios.get(`${API}/offerte-mese/catalogo/promo?${promoParams.toString()}`, { headers: getAuthHeader() });
          setPromoOffers(promoRes.data || []);
        } catch { setPromoOffers([]); }
      } else {
        setPromoOffers([]);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, tipoVeicolo, selectedMarca, selectedModello, selectedVersione, showNuoviArrivi, useCustomSearch, custom4Vantage, customFuel, customSegmento, customBudget]);

  // Handle tipo change - only reset if NOT restoring saved filters
  useEffect(() => {
    // Skip reset on initial mount when restoring saved filters
    if (isInitialMount.current && hasSavedFilters && savedFilters?.tipoVeicolo === tipoVeicolo) {
      // We're restoring - don't reset, but DO fetch data
      fetchMarche();
      fetchAllVehiclesForBudget();
      
      // Restore modelli and versioni dropdowns if we had saved selections
      if (savedFilters?.selectedMarca) {
        fetchModelli(savedFilters.selectedMarca);
        if (savedFilters?.selectedModello) {
          fetchVersioni(savedFilters.selectedMarca, savedFilters.selectedModello);
        }
      }
      return;
    }
    
    // Normal tipo change - reset everything
    setSelectedMarca('');
    setSelectedModello('');
    setSelectedVersione('');
    setAvailableModelli([]);
    setAvailableVersioni([]);
    // Reset custom search
    setCustomKm('');
    setCustomDurata('');
    setCustomFuel('');
    setCustomBudget('');
    fetchMarche();
    fetchAllVehiclesForBudget();
  }, [tipoVeicolo, fetchMarche, fetchAllVehiclesForBudget, hasSavedFilters, savedFilters, fetchModelli, fetchVersioni]);

  // Fetch modelli when marca changes - don't reset if restoring
  useEffect(() => {
    // Skip reset on initial mount when restoring saved filters
    if (isInitialMount.current && hasSavedFilters && savedFilters?.selectedMarca === selectedMarca) {
      return; // Will be handled by tipoVeicolo effect
    }
    
    setSelectedModello('');
    setSelectedVersione('');
    setAvailableVersioni([]);
    if (selectedMarca) {
      fetchModelli(selectedMarca);
    } else {
      setAvailableModelli([]);
    }
  }, [selectedMarca, fetchModelli, hasSavedFilters, savedFilters]);

  // Fetch versioni when modello changes - don't reset if restoring
  useEffect(() => {
    // Skip reset on initial mount when restoring saved filters
    if (isInitialMount.current && hasSavedFilters && savedFilters?.selectedModello === selectedModello) {
      return; // Will be handled by tipoVeicolo effect
    }
    
    setSelectedVersione('');
    if (selectedMarca && selectedModello) {
      fetchVersioni(selectedMarca, selectedModello);
    } else {
      setAvailableVersioni([]);
    }
  }, [selectedMarca, selectedModello, fetchVersioni, hasSavedFilters, savedFilters]);

  // Fetch sotto-categorie quando cambia il segmento selezionato
  useEffect(() => {
    if (customSegmento) {
      fetchSottocategorie(customSegmento);
    } else {
      // Reset sotto-categorie se nessun segmento selezionato
      setAvailableSegmentiMercato([]);
      setAvailableSegmentDescriptions([]);
    }
  }, [customSegmento, fetchSottocategorie]);

  // Fetch vehicles when any filter changes
  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Helper: filter vehicles by budget criteria
  const filterByBudget = useCallback((vehicleList) => {
    let filtered = vehicleList;
    
    // Filter by fuel type (MATCH ESATTO, non parziale)
    if (customFuel) {
      filtered = filtered.filter(v => 
        v.fuel?.toLowerCase() === customFuel.toLowerCase()
      );
    }
    
    // Filter by neopatentati
    if (customNeopatentati) {
      filtered = filtered.filter(v => v.neopatentati === true);
    }
    
    // Filter by 4Vantage (only vehicles with canone_4vantage > 0)
    if (custom4Vantage) {
      filtered = filtered.filter(v => v.canone_4vantage && v.canone_4vantage > 0);
    }
    
    // Filter by budget if km and durata are selected
    if (customKm && customDurata && customBudget) {
      const budget = parseFloat(customBudget);
      const budgetMin = budget - BUDGET_TOLERANCE;
      const budgetMax = budget + BUDGET_TOLERANCE;
      
      // For 4Vantage, use the canone_4vantage field directly
      if (custom4Vantage) {
        filtered = filtered.filter(v => {
          const quote = v.canone_4vantage;
          if (!quote || quote <= 0) return false;
          return quote >= budgetMin && quote <= budgetMax;
        });
      } else {
        const quoteKey = `${customDurata}_${customKm}`;
        filtered = filtered.filter(v => {
          const quote = v.quotations?.[quoteKey];
          if (!quote || quote <= 0) return false;
          return quote >= budgetMin && quote <= budgetMax;
        });
      }
    }
    
    return filtered;
  }, [customKm, customDurata, customBudget, customFuel, customNeopatentati, custom4Vantage]);

  // Get budget-filtered marche (from all vehicles)
  const budgetFilteredMarche = useMemo(() => {
    if (!useCustomSearch || (!customBudget && !customFuel && !customNeopatentati && !custom4Vantage)) {
      return availableMarche;
    }
    const filteredVehicles = filterByBudget(allVehiclesForBudget);
    const marche = [...new Set(filteredVehicles.map(v => v.marca))].sort();
    return marche;
  }, [useCustomSearch, customBudget, customFuel, customNeopatentati, custom4Vantage, allVehiclesForBudget, availableMarche, filterByBudget]);

  // Get budget-filtered modelli (based on selected marca)
  const budgetFilteredModelli = useMemo(() => {
    if (!useCustomSearch || (!customBudget && !customFuel && !customNeopatentati && !custom4Vantage)) {
      return availableModelli;
    }
    const filteredVehicles = filterByBudget(allVehiclesForBudget.filter(v => v.marca === selectedMarca));
    const modelli = [...new Set(filteredVehicles.map(v => v.modello))].sort();
    return modelli;
  }, [useCustomSearch, customBudget, customFuel, customNeopatentati, custom4Vantage, selectedMarca, allVehiclesForBudget, availableModelli, filterByBudget]);

  // Get budget-filtered versioni (based on selected marca and modello)
  const budgetFilteredVersioni = useMemo(() => {
    if (!useCustomSearch || (!customBudget && !customFuel && !customNeopatentati && !custom4Vantage)) {
      return availableVersioni;
    }
    const filteredVehicles = filterByBudget(
      allVehiclesForBudget.filter(v => v.marca === selectedMarca && v.modello === selectedModello)
    );
    const versioni = [...new Set(filteredVehicles.map(v => v.versione))].sort();
    return versioni;
  }, [useCustomSearch, customBudget, customFuel, customNeopatentati, custom4Vantage, selectedMarca, selectedModello, allVehiclesForBudget, availableVersioni, filterByBudget]);

  // Apply custom search filter
  useEffect(() => {
    if (!useCustomSearch || !vehicles.length) {
      setFilteredVehicles(vehicles);
      return;
    }

    let filtered = [...vehicles];

    // Filter by fuel type (MATCH ESATTO)
    if (customFuel) {
      filtered = filtered.filter(v => 
        v.fuel?.toLowerCase() === customFuel.toLowerCase()
      );
    }
    
    // Filter by segmento mercato
    if (customSegmento) {
      filtered = filtered.filter(v => 
        v.segmento?.toLowerCase() === customSegmento.toLowerCase()
      );
    }
    
    // Filter by neopatentati
    if (customNeopatentati) {
      filtered = filtered.filter(v => v.neopatentati === true);
    }
    
    // Filter by 4Vantage
    if (custom4Vantage) {
      filtered = filtered.filter(v => v.canone_4vantage && v.canone_4vantage > 0);
    }

    // Filter by budget if km and durata are selected
    if (customKm && customDurata && customBudget) {
      const budget = parseFloat(customBudget);
      const budgetMin = budget - BUDGET_TOLERANCE;
      const budgetMax = budget + BUDGET_TOLERANCE;
      
      if (custom4Vantage) {
        // For 4Vantage, filter by canone_4vantage
        filtered = filtered.filter(v => {
          const quote = v.canone_4vantage;
          if (!quote || quote <= 0) return false;
          return quote >= budgetMin && quote <= budgetMax;
        });
      } else {
        const quoteKey = `${customDurata}_${customKm}`;
        filtered = filtered.filter(v => {
          const quote = v.quotations?.[quoteKey];
          if (!quote || quote <= 0) return false;
          return quote >= budgetMin && quote <= budgetMax;
        });
      }
    }

    setFilteredVehicles(filtered);
  }, [useCustomSearch, vehicles, customKm, customDurata, customFuel, customBudget, customNeopatentati, custom4Vantage, customSegmento]);

  // Get displayed vehicles
  const displayedVehicles = useCustomSearch ? filteredVehicles : vehicles;

  const handleMarcaChange = (value) => {
    setSelectedMarca(value === 'reset' ? '' : value);
  };

  const handleModelloChange = (value) => {
    setSelectedModello(value === 'reset' ? '' : value);
  };

  const handleVersioneChange = (value) => {
    setSelectedVersione(value === 'reset' ? '' : value);
  };

  const resetFilters = () => {
    setSelectedMarca('');
    setSelectedModello('');
    setSelectedVersione('');
    // Clear session storage for marca/modello/versione
    const currentFilters = getSavedFilters() || {};
    saveFilters({
      ...currentFilters,
      selectedMarca: '',
      selectedModello: '',
      selectedVersione: ''
    });
  };

  const resetCustomSearch = () => {
    setCustomKm('');
    setCustomDurata('');
    setCustomFuel('');
    setCustomBudget('');
    setCustomNeopatentati(false);
    setCustom4Vantage(false);
    // Clear session storage for custom search
    const currentFilters = getSavedFilters() || {};
    saveFilters({
      ...currentFilters,
      customKm: '',
      customDurata: '',
      customFuel: '',
      customBudget: '',
      customNeopatentati: false,
      custom4Vantage: false
    });
  };

  const resetAllFilters = () => {
    handleResetFilters();
  };

  const getMinQuote = (quotations) => {
    if (!quotations || Object.keys(quotations).length === 0) return null;
    const values = Object.values(quotations).filter(v => v > 0);
    return values.length > 0 ? Math.min(...values) : null;
  };

  return (
    <div className="space-y-6" data-testid="catalog-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Catalogo Veicoli
          </h1>
          <p className="text-slate-500 mt-1">Seleziona il tipo, marca, modello e versione</p>
        </div>
        {hasActiveFilters && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={resetAllFilters}
            className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
            data-testid="reset-all-filters-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Azzera Filtri
          </Button>
        )}
      </div>

      {/* Tipo Veicolo Selector */}
      <Card>
        <CardContent className="p-4">
          <Tabs value={tipoVeicolo} onValueChange={setTipoVeicolo} className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger 
                value="nuovo" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white"
                data-testid="tab-nuovo"
              >
                <Car className="w-4 h-4 mr-2" />
                NUOVO
              </TabsTrigger>
              <TabsTrigger 
                value="usato"
                className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
                data-testid="tab-usato"
              >
                <Gauge className="w-4 h-4 mr-2" />
                USATO
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Cascading Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Car className="w-5 h-5 text-primary" />
            <span className="font-semibold text-slate-700">Seleziona il veicolo</span>
            <Badge variant={tipoVeicolo === 'nuovo' ? 'default' : 'secondary'} className={tipoVeicolo === 'usato' ? 'bg-emerald-600' : ''}>
              {tipoVeicolo.toUpperCase()}
            </Badge>
          </div>
          
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            {/* Step 1: Marca */}
            <div className="flex-1 w-full lg:w-auto">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">
                1. Marca {useCustomSearch && customBudget && <span className="text-amber-600">({budgetFilteredMarche.length})</span>}
              </label>
              <Select value={selectedMarca} onValueChange={handleMarcaChange}>
                <SelectTrigger className="bg-white w-full" data-testid="filter-marca">
                  <SelectValue placeholder="Seleziona marca..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reset">-- Tutte le marche --</SelectItem>
                  {budgetFilteredMarche.map(marca => (
                    <SelectItem key={marca} value={marca}>{marca}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <ChevronRight className="hidden lg:block w-5 h-5 text-slate-300 mt-6" />

            {/* Step 2: Modello */}
            <div className="flex-1 w-full lg:w-auto">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">
                2. Modello {useCustomSearch && customBudget && selectedMarca && <span className="text-amber-600">({budgetFilteredModelli.length})</span>}
              </label>
              <Select 
                value={selectedModello} 
                onValueChange={handleModelloChange}
                disabled={!selectedMarca}
              >
                <SelectTrigger 
                  className={`bg-white w-full ${!selectedMarca ? 'opacity-50' : ''}`} 
                  data-testid="filter-modello"
                >
                  <SelectValue placeholder={selectedMarca ? "Seleziona modello..." : "Prima seleziona marca"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reset">-- Tutti i modelli --</SelectItem>
                  {budgetFilteredModelli.map(modello => (
                    <SelectItem key={modello} value={modello}>{modello}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <ChevronRight className="hidden lg:block w-5 h-5 text-slate-300 mt-6" />

            {/* Step 3: Versione */}
            <div className="flex-[2] w-full lg:w-auto">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">
                3. Versione {useCustomSearch && customBudget && selectedModello && <span className="text-amber-600">({budgetFilteredVersioni.length})</span>}
              </label>
              <Select 
                value={selectedVersione} 
                onValueChange={handleVersioneChange}
                disabled={!selectedModello}
              >
                <SelectTrigger 
                  className={`bg-white w-full ${!selectedModello ? 'opacity-50' : ''}`} 
                  data-testid="filter-versione"
                >
                  <SelectValue placeholder={selectedModello ? "Seleziona versione..." : "Prima seleziona modello"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reset">-- Tutte le versioni --</SelectItem>
                  {budgetFilteredVersioni.map(versione => (
                    <SelectItem key={versione} value={versione}>
                      <span className="truncate max-w-[400px] block">{versione}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <span className="text-sm text-slate-500">
              <span className="font-semibold text-primary">{displayedVehicles.length}</span> veicoli {tipoVeicolo === 'usato' ? 'usati' : 'nuovi'} trovati
              {useCustomSearch && customBudget && (
                <span className="text-xs text-slate-400 ml-2">
                  (budget €{(parseFloat(customBudget) - BUDGET_TOLERANCE).toLocaleString('it-IT')} - €{(parseFloat(customBudget) + BUDGET_TOLERANCE).toLocaleString('it-IT')})
                </span>
              )}
              {useCustomSearch && custom4Vantage && (
                <span className="text-xs text-violet-600 ml-2 font-medium">• 4VANTAGE</span>
              )}
              {useCustomSearch && customNeopatentati && (
                <span className="text-xs text-amber-600 ml-2 font-medium">• Neopatentati</span>
              )}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={resetFilters} 
              data-testid="reset-filters-btn"
              disabled={!selectedMarca && !selectedModello && !selectedVersione}
            >
              Resetta filtri
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* NUOVO: Filtro rapido "Nuovi Arrivi" */}
      <div className="flex flex-wrap gap-3 items-center">
        <Button
          variant={showNuoviArrivi ? "default" : "outline"}
          size="sm"
          onClick={() => setShowNuoviArrivi(!showNuoviArrivi)}
          className={`${showNuoviArrivi 
            ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white border-0 shadow-md' 
            : 'border-rose-300 text-rose-600 hover:bg-rose-50'
          }`}
          data-testid="filter-nuovi-arrivi"
        >
          <Sparkles className={`w-4 h-4 mr-1.5 ${showNuoviArrivi ? 'animate-pulse' : ''}`} />
          Nuovi Arrivi
          {showNuoviArrivi && <span className="ml-1.5 text-[10px] bg-white/20 px-1.5 rounded">ON</span>}
        </Button>
        
        {showNuoviArrivi && (
          <span className="text-xs text-slate-500 italic">
            Mostra solo veicoli quotati negli ultimi 2 giorni
          </span>
        )}
      </div>

      {/* Personalizza la tua richiesta */}
      <Card className={`border-2 transition-colors ${useCustomSearch ? 'border-amber-400 bg-amber-50/30' : 'border-slate-200'}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${useCustomSearch ? 'bg-amber-500' : 'bg-slate-100'}`}>
                <SlidersHorizontal className={`w-5 h-5 ${useCustomSearch ? 'text-white' : 'text-slate-500'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  Personalizza la tua richiesta
                </h3>
                <p className="text-xs text-slate-500">Trova il veicolo ideale in base al tuo budget</p>
              </div>
            </div>
            <Switch
              checked={useCustomSearch}
              onCheckedChange={setUseCustomSearch}
              data-testid="custom-search-toggle"
            />
          </div>

          {useCustomSearch && (
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Km Annui */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Route className="w-4 h-4 text-amber-600" />
                    Km annui previsti
                  </Label>
                  <Select value={customKm} onValueChange={setCustomKm}>
                    <SelectTrigger className="bg-white" data-testid="custom-km">
                      <SelectValue placeholder="Seleziona km..." />
                    </SelectTrigger>
                    <SelectContent>
                      {KM_OPTIONS.map(km => (
                        <SelectItem key={km} value={km.toString()}>
                          {km.toLocaleString('it-IT')} km/anno
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Durata */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-amber-600" />
                    Durata noleggio
                  </Label>
                  <Select value={customDurata} onValueChange={setCustomDurata}>
                    <SelectTrigger className="bg-white" data-testid="custom-durata">
                      <SelectValue placeholder="Seleziona durata..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATIONS.map(dur => (
                        <SelectItem key={dur} value={dur.toString()}>
                          {dur} mesi
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Alimentazione */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Fuel className="w-4 h-4 text-amber-600" />
                    Alimentazione
                  </Label>
                  <Select value={customFuel} onValueChange={(v) => setCustomFuel(v === 'all' ? '' : v)}>
                    <SelectTrigger className="bg-white" data-testid="custom-fuel">
                      <SelectValue placeholder="Tutte..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">-- Tutte --</SelectItem>
                      {availableFuels.map(fuel => (
                        <SelectItem key={fuel} value={fuel}>{fuel}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Segmento Mercato */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Car className="w-4 h-4 text-amber-600" />
                    Segmento Mercato
                  </Label>
                  <Select value={customSegmento} onValueChange={(v) => setCustomSegmento(v === 'all' ? '' : v)}>
                    <SelectTrigger className="bg-white" data-testid="custom-segmento">
                      <SelectValue placeholder="Tutti..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">-- Tutti --</SelectItem>
                      {availableSegmenti.map(seg => (
                        <SelectItem key={seg} value={seg}>{seg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Sotto-categorie del segmento selezionato */}
                  {customSegmento && (
                    <div className="pt-2">
                      <p className="text-xs text-slate-500 mb-1.5">Categorie disponibili:</p>
                      <div className="flex flex-wrap gap-1">
                        {/* Per NUOVO: mostra segmenti_mercato, per USATO: mostra segment_descriptions */}
                        {(tipoVeicolo === 'nuovo' ? availableSegmentiMercato : availableSegmentDescriptions).map(cat => (
                          <Badge 
                            key={cat} 
                            variant="outline" 
                            className="text-[10px] px-1.5 py-0.5 cursor-default border-amber-300 text-amber-700"
                          >
                            {cat}
                          </Badge>
                        ))}
                        {((tipoVeicolo === 'nuovo' ? availableSegmentiMercato : availableSegmentDescriptions).length === 0) && (
                          <span className="text-[10px] text-slate-400 italic">Nessuna sottocategoria</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Budget */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm">
                    <Euro className="w-4 h-4 text-amber-600" />
                    Budget mensile (€) <span className="text-xs text-slate-400">±{BUDGET_TOLERANCE}€</span>
                  </Label>
                  <Input
                    type="number"
                    min="100"
                    step="50"
                    placeholder="es. 400"
                    value={customBudget}
                    onChange={(e) => setCustomBudget(e.target.value)}
                    className="bg-white"
                    data-testid="custom-budget"
                  />
                </div>
              </div>

              {/* Neopatentati checkbox */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-4 border-t border-slate-200 mt-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="neopatentati"
                    checked={customNeopatentati}
                    onCheckedChange={setCustomNeopatentati}
                    data-testid="custom-neopatentati"
                  />
                  <Label htmlFor="neopatentati" className="flex items-center gap-2 cursor-pointer">
                    <GraduationCap className="w-4 h-4 text-amber-600" />
                    <span className="text-sm">Solo veicoli per <strong>Neopatentati</strong></span>
                  </Label>
                </div>
                
                {/* 4Vantage checkbox - only for NUOVO */}
                {tipoVeicolo === 'nuovo' && (
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="4vantage"
                      checked={custom4Vantage}
                      onCheckedChange={setCustom4Vantage}
                      data-testid="custom-4vantage"
                      className="border-violet-400 data-[state=checked]:bg-violet-600"
                    />
                    <Label htmlFor="4vantage" className="flex flex-col cursor-pointer">
                      <span className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-violet-600" />
                        <strong className="text-violet-700">Offerta 4VANTAGE</strong>
                      </span>
                      <span className="text-xs text-violet-500">solo per privati e P.IVA forfettaria</span>
                    </Label>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-4">
                <div className="text-sm text-amber-700">
                  {customKm && customDurata && customBudget ? (
                    <span>
                      Cercando veicoli{custom4Vantage ? ' 4VANTAGE' : ''}{customNeopatentati ? ' per neopatentati' : ''} con canone tra <strong>€{(parseFloat(customBudget) - BUDGET_TOLERANCE).toLocaleString('it-IT')}</strong> e <strong>€{(parseFloat(customBudget) + BUDGET_TOLERANCE).toLocaleString('it-IT')}</strong>
                      {!custom4Vantage && ` per ${customDurata} mesi e ${parseInt(customKm).toLocaleString('it-IT')} km/anno`}
                      {custom4Vantage && ' (48 mesi, 30.000 km fissi)'}
                    </span>
                  ) : custom4Vantage ? (
                    <span className="text-violet-700">Mostrando solo veicoli con offerta <strong>4VANTAGE</strong> (48 mesi, 30.000 km)</span>
                  ) : customNeopatentati ? (
                    <span>Mostrando solo veicoli per neopatentati</span>
                  ) : (
                    <span className="text-slate-500">Seleziona km, durata e budget per filtrare i veicoli</span>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={resetCustomSearch}
                  disabled={!customKm && !customDurata && !customFuel && !customBudget && !customNeopatentati && !custom4Vantage}
                >
                  Resetta ricerca
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vehicle Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="h-64 animate-pulse">
              <CardContent className="p-6">
                <div className="h-full bg-slate-100 rounded-lg" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : displayedVehicles.length > 0 || promoOffers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Promo Offerte del Mese in cima (filtrate per pertinenza budget) */}
          {promoOffers.filter(promo => {
            // Se c'è un filtro budget attivo, mostra solo promo il cui range include il budget
            if (customBudget && useCustomSearch) {
              const budget = parseFloat(customBudget);
              if (!isNaN(budget)) {
                const quotes = promo.quotations ? Object.values(promo.quotations).filter(v => v > 0) : [];
                if (quotes.length === 0) return false;
                const minQ = Math.min(...quotes);
                const maxQ = Math.max(...quotes);
                // Tolleranza 10% sul budget per non escludere offerte ai margini
                return budget >= minQ * 0.9 && budget <= maxQ * 1.1;
              }
            }
            return true;
          }).map(promo => (
            <PromoCardCatalog key={`promo-${promo.id}`} promo={promo} navigate={navigate} />
          ))}
          {displayedVehicles.map(vehicle => {
            const minQuote = getMinQuote(vehicle.quotations);
            // Show specific quote if custom search is active
            const customQuoteKey = customKm && customDurata ? `${customDurata}_${customKm}` : null;
            // FIX 4VANTAGE: Se 4Vantage è attivo, usa direttamente canone_4vantage
            const customQuote = custom4Vantage && vehicle.canone_4vantage 
              ? vehicle.canone_4vantage 
              : (customQuoteKey ? vehicle.quotations?.[customQuoteKey] : null);
            const isUsato = vehicle.tipo === 'usato';
            const isSold = vehicle.is_sold || vehicle.status === 'sold';
            
            // Parametri display per 4Vantage (fissi: 48 mesi, 30.000 km totali = 7.500 km/anno)
            const display4VantageParams = custom4Vantage && vehicle.canone_4vantage;
            
            return (
              <Card 
                key={vehicle.id} 
                className={`group overflow-hidden relative ${
                  isSold 
                    ? 'border-red-300 opacity-75 grayscale-[50%] cursor-not-allowed' 
                    : `card-hover ${isUsato ? 'border-emerald-200' : ''} ${useCustomSearch && customQuote ? 'ring-2 ring-amber-400' : ''} ${display4VantageParams ? 'ring-2 ring-violet-400' : ''} ${(vehicle.opzionato_ae || vehicle.opzionato_af || vehicle.opzionato_aj) ? 'border-amber-400 ring-1 ring-amber-300' : ''}`
                }`}
                data-testid={`vehicle-card-${vehicle.id}`}
              >
                {/* TIMBRO VENDUTO */}
                {isSold && (
                  <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center">
                    <div 
                      className="transform -rotate-12 border-8 border-red-600 rounded-lg px-6 py-2 bg-red-600/10 backdrop-blur-[1px]"
                      style={{
                        boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)'
                      }}
                    >
                      <span 
                        className="text-red-600 font-black text-3xl sm:text-4xl tracking-wider uppercase"
                        style={{
                          textShadow: '2px 2px 0 white, -2px -2px 0 white, 2px -2px 0 white, -2px 2px 0 white',
                          fontFamily: 'Impact, Haettenschweiler, sans-serif'
                        }}
                      >
                        VENDUTO
                      </span>
                    </div>
                  </div>
                )}
                
                <CardContent className="p-0">
                  {/* Hero Image from Drive (solo NUOVO) */}
                  <CatalogVehicleHero marca={vehicle.marca} modello={vehicle.modello} isUsato={isUsato} />
                  
                  {/* Header with gradient */}
                  <div className={`px-6 py-3 relative ${isUsato ? 'bg-gradient-to-br from-emerald-50 to-emerald-100/50' : 'bg-gradient-to-br from-primary/10 to-primary/5'}`}>
                    {/* Badge NUOVO ARRIVO (ultimi 3 giorni) */}
                    {vehicle.is_nuovo_arrivo && !isSold && (
                      <div className="absolute top-2 left-2 z-20">
                        <Badge className="bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[10px] px-2 py-0.5 font-bold shadow-md animate-pulse">
                          ✨ NEW
                        </Badge>
                      </div>
                    )}
                    
                    {/* Badge OPZIONATO per tutti i tipi (USATO, NUOVO, 4VANTAGE) */}
                    {(vehicle.opzionato_ae || vehicle.opzionato_af || vehicle.opzionato_aj) && !isSold && (
                      <div className="absolute top-2 right-2 z-10">
                        <OpzionatoBadge 
                          opzionato_ae={vehicle.opzionato_ae} 
                          opzionato_af={vehicle.opzionato_af}
                          opzionato_aj={vehicle.opzionato_aj}
                          variant="badge"
                        />
                      </div>
                    )}
                    
                    {/* Badge UNITÀ DISPONIBILI - mostra il numero quando >1 unità identiche
                          Colore: viola per 4Vantage, blu per NUOVO, verde per USATO */}
                    {(() => {
                      // Calcola conteggio: 4Vantage usa units_available, NUOVO/USATO usano stock_available
                      const count4v = vehicle.units_available || 1;
                      const countStock = vehicle.stock_available || 1;
                      const showCount = display4VantageParams ? count4v : countStock;
                      if (showCount <= 1 || isSold) return null;
                      const badgeColor = display4VantageParams
                        ? 'bg-violet-600'
                        : (isUsato ? 'bg-emerald-600' : 'bg-blue-600');
                      return (
                        <div className="absolute bottom-2 right-2 z-10">
                          <Badge className={`${badgeColor} text-white text-[10px] px-2 py-0.5 font-bold shadow-md`}>
                            <Users className="w-3 h-3 mr-1 inline" />
                            {showCount} disponibili
                          </Badge>
                        </div>
                      );
                    })()}
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`text-sm font-medium ${isUsato ? 'text-emerald-700' : 'text-primary'}`}>{vehicle.marca}</p>
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isUsato ? 'border-emerald-500 text-emerald-700' : ''}`}>
                            {isUsato ? 'USATO' : 'NUOVO'}
                          </Badge>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                          {vehicle.modello}
                        </h3>
                      </div>
                      {vehicle.link_immagine && (
                        <a href={vehicle.link_immagine} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/80 shadow-sm flex items-center justify-center hover:bg-slate-50">
                          <ImageIcon className="w-4 h-4 text-emerald-600" />
                        </a>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{vehicle.versione}</p>
                  </div>

                  {/* Details */}
                  <div className="p-6 pt-4">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {/* Badge Segmento specifico (sottocategoria) */}
                      {(vehicle.segmento_mercato || vehicle.segment_description) && (
                        <Badge variant="outline" className="flex items-center gap-1 border-slate-300 text-slate-600">
                          <Car className="w-3 h-3" />
                          {isUsato ? vehicle.segment_description : vehicle.segmento_mercato}
                        </Badge>
                      )}
                      
                      {/* Badge Fuel con dettaglio tecnico (solo NUOVO) */}
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Fuel className="w-3 h-3" />
                        {vehicle.fuel}
                        {!isUsato && vehicle.fuel_code && (
                          <span className="text-[10px] text-slate-500 ml-1">({vehicle.fuel_code})</span>
                        )}
                      </Badge>
                      
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Settings2 className="w-3 h-3" />
                        {vehicle.cambio}
                      </Badge>
                      {isUsato && vehicle.km && (
                        <Badge variant="outline" className="flex items-center gap-1 border-emerald-300 text-emerald-700">
                          <Gauge className="w-3 h-3" />
                          {vehicle.km.toLocaleString('it-IT')} km
                        </Badge>
                      )}
                    </div>

                    {vehicle.disponibilita && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mb-4">
                        <Calendar className="w-3 h-3" />
                        {isUsato ? 'Immatricolazione' : 'Disponibile'}: {vehicle.immatricolazione || vehicle.disponibilita}
                      </p>
                    )}

                    <div className="flex items-end justify-between">
                      <div>
                        {/* 4VANTAGE: Mostra parametri fissi e canone 4Vantage */}
                        {display4VantageParams ? (
                          <>
                            <p className="text-xs text-violet-600 font-medium">48 mesi • 30.000 km tot. • 4VANTAGE</p>
                            <p className="text-xl font-bold text-violet-600">
                              €{vehicle.canone_4vantage.toLocaleString('it-IT')}<span className="text-sm font-normal">/mese</span>
                            </p>
                          </>
                        ) : useCustomSearch && customQuote ? (
                          <>
                            <p className="text-xs text-amber-600 font-medium">{customDurata} mesi • {parseInt(customKm).toLocaleString('it-IT')} km</p>
                            <p className={`text-xl font-bold text-amber-600`}>
                              €{customQuote.toLocaleString('it-IT')}<span className="text-sm font-normal">/mese</span>
                            </p>
                          </>
                        ) : minQuote ? (
                          <>
                            <p className="text-xs text-slate-500">A partire da</p>
                            <p className={`text-xl font-bold ${isUsato ? 'text-emerald-600' : 'text-primary'}`}>
                              €{minQuote.toLocaleString('it-IT')}<span className="text-sm font-normal">/mese</span>
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-slate-400">Quotazione non disponibile</p>
                        )}
                      </div>
                      {isSold ? (
                        <Button 
                          size="sm" 
                          disabled
                          className="bg-slate-400 cursor-not-allowed opacity-50"
                          data-testid={`view-vehicle-${vehicle.id}`}
                        >
                          Non Disponibile
                        </Button>
                      ) : (
                        <Link 
                          to={`/dashboard/veicolo/${vehicle.id}`}
                          state={{ 
                            selectedDuration: useCustomSearch && customDurata ? parseInt(customDurata) : null,
                            selectedKm: useCustomSearch && customKm ? parseInt(customKm) : null,
                            use4Vantage: useCustomSearch && custom4Vantage
                          }}
                        >
                          <Button 
                            size="sm" 
                            className={`btn-scale ${isUsato ? 'bg-emerald-600 hover:bg-emerald-700' : ''} ${display4VantageParams ? 'bg-violet-600 hover:bg-violet-700' : ''}`}
                            data-testid={`view-vehicle-${vehicle.id}`}
                            onClick={() => trackEvent('view_vehicle_detail', vehicle.id, getAuthHeader)}
                          >
                            Dettagli <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            {useCustomSearch ? (
              <>
                <SlidersHorizontal className="w-16 h-16 mx-auto text-amber-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-800">Nessun veicolo trovato per il tuo budget</h3>
                <p className="text-slate-500 mt-1">
                  {customBudget 
                    ? `Nessun veicolo con canone tra €${(parseFloat(customBudget) - BUDGET_TOLERANCE)} e €${(parseFloat(customBudget) + BUDGET_TOLERANCE)} per la configurazione selezionata`
                    : "Completa i parametri di ricerca (km, durata e budget)"}
                </p>
                <div className="flex gap-2 justify-center mt-4">
                  <Button variant="outline" onClick={resetCustomSearch}>
                    Resetta ricerca personalizzata
                  </Button>
                  <Button variant="ghost" onClick={() => setUseCustomSearch(false)}>
                    Torna alla ricerca standard
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Car className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-800">Nessun veicolo {tipoVeicolo === 'usato' ? 'usato' : 'nuovo'} trovato</h3>
                <p className="text-slate-500 mt-1">
                  {selectedMarca 
                    ? "Prova a modificare i filtri di ricerca" 
                    : "Seleziona una marca per iniziare la ricerca"}
                </p>
                {(selectedMarca || selectedModello || selectedVersione) && (
                  <Button variant="outline" className="mt-4" onClick={resetFilters}>
                    Resetta filtri
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CatalogPage;
